import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Smartphone, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV_LINKS, SERVICES, LOGO_URL } from "@/data/site";
import { IS_STATIC } from "@/lib/api";

export const Footer = () => {
  return (
    <footer className="relative bg-brand text-white overflow-hidden" data-testid="footer">
      <div className="grain absolute inset-0" />
      <div className="container-x relative pt-20 pb-10">
        {/* Big CTA line */}
        <div className="border-b border-white/10 pb-14 mb-14">
          <p className="text-brand-sky text-sm font-semibold uppercase tracking-[0.25em] mb-4">Ready when you are</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <h2 className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl">
              Let&rsquo;s move <br /> your world.
            </h2>
            <Link
              to="/#quote"
              className="group inline-flex items-center gap-3 self-start bg-white text-brand rounded-full px-7 py-4 font-semibold hover:bg-brand-light transition-colors"
              data-testid="footer-quote-cta"
            >
              Request a Quote
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>

        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="logo" className="h-12 w-12 rounded-full ring-1 ring-white/20" />
              <span className="font-heading font-bold text-lg">Agarwal Secure</span>
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              Trusted packers &amp; movers since {COMPANY.established}. Reliable, safe and affordable relocation and logistics across India.
            </p>
          </div>

          <div>
            <h4 className="font-heading font-bold mb-5 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/70 hover:text-white text-sm transition-colors" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold mb-5 text-white">Services</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.title}>
                  <Link to="/services" className="text-white/70 hover:text-white text-sm transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold mb-5 text-white">Get in Touch</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex gap-3">
                <MapPin className="h-5 w-5 text-brand-sky shrink-0" />
                <span className="text-white/70">{COMPANY.address}</span>
              </li>
              <li className="flex gap-3 items-center">
                <Smartphone className="h-5 w-5 text-brand-sky shrink-0" />
                <span className="text-white/70">
                  {COMPANY.mobiles.map((m, i) => (
                    <a key={m} href={`tel:${m}`} className="hover:text-white transition-colors">
                      {m}{i < COMPANY.mobiles.length - 1 ? ", " : ""}
                    </a>
                  ))}
                </span>
              </li>
              <li className="flex gap-3 items-center">
                <Phone className="h-5 w-5 text-brand-sky shrink-0" />
                <a href={`tel:${COMPANY.phone}`} className="text-white/70 hover:text-white transition-colors">{COMPANY.phone}</a>
              </li>
              <li className="flex gap-3 items-center">
                <Mail className="h-5 w-5 text-brand-sky shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="text-white/70 hover:text-white transition-colors break-all">{COMPANY.email}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/60">
          <p>© {new Date().getFullYear()} {COMPANY.name}. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link>
            {!IS_STATIC && (
              <Link to="/admin/login" className="hover:text-white transition-colors" data-testid="footer-admin">Staff Login</Link>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};
